import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import InstallmentsView from "@/views/InstallmentsView.vue";
import PaymentProvidersView from "@/views/PaymentProvidersView.vue";
import { trackRouter } from "vue-gtag-next";
import { getInstallments } from "@/services/DataService";
import { datadogRum } from "@datadog/browser-rum";
import { identifyDevice } from "@/customerio";
import packageJson from "@/../package.json";
import { store } from "@/store";
import OtherAmountEntryView from "@/views/OtherAmountEntryView.vue";
import SuperPacmanInstallmentsView from "@/views/SuperPacmanInstallmentsView.vue";
import { Countries } from "@/types/Countries";
import PaymentSuccessView from "@/views/PaymentSuccessView.vue";
import PaymentFailedView from "@/views/PaymentFailedView.vue";
import ExtensionTypeView from "@/views/ExtensionTypeView.vue";
import authHelper from "@/helpers/authHelper";
import ExtensionReasonView from "@/views/ExtensionReasonView.vue";
import V1InstallmentsView from "@/views/V1InstallmentsView.vue";
import V1PaymentProvidersView from "@/views/V1PaymentProvidersView.vue";
import V1ProviderGroupView from "@/views/V1ProviderGroupView.vue";
import V1OtherAmountView from "@/views/V1OtherAmountView.vue";
import V1PaymentReferenceView from "@/views/V1PaymentReferenceView.vue";
import { ensureV1DevAuth } from "@/helpers/v1TestHelper";

const sampleRate = process.env.VUE_APP_K8_ENVIRONMENT_NAME === "staging" ? 100 : 3;
const sessionReplayRate = process.env.VUE_APP_K8_ENVIRONMENT_NAME === "staging" ? 50 : 5;

const initializeDatadogRum = (country: string | undefined) => {
  if (country === Countries.MEXICO && !datadogRum.getInitConfiguration()) {
    console.log("Initializing Datadog RUM");
    datadogRum.init({
      applicationId: process.env.VUE_APP_DATADOG_APPLICATION_ID as string,
      clientToken: process.env.VUE_APP_DATADOG_CLIENT_TOKEN as string,
      site: "datadoghq.com",
      service: "payoptions",
      env: process.env.VUE_APP_K8_ENVIRONMENT_NAME as string,
      version: packageJson.version,
      sessionSampleRate: sampleRate,
      sessionReplaySampleRate: sessionReplayRate,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask-user-input",
    });
    console.log("Datadog RUM initialized successfully");
  }
};

const fetchInstallmentsData = async (deviceTag: string, option: string) => {
  const { installments, error } = await getInstallments({ deviceTag, paymentOption: option });
  store.dispatch("payOptions/setInstallments", installments);

  if (error.value) {
    store.dispatch("payOptions/setError", error.value);
    throw new Error("Failed to fetch installments");
  }
  console.log("Installments data fetched successfully");
};

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "root",
    component: () => import("@/components/HelloI18n.vue"),
    props: true,
  },
  {
    path: "/version",
    name: "Version",
    component: () => import("@/components/HelloI18n.vue"),
  },
  {
    path: "/device/:deviceTag",
    name: "InstallmentsByDevice",
    component: InstallmentsView,
    props: true,
    beforeEnter: async (to, from, next) => {
      try {
        const deviceTag = to.params.deviceTag as string;
        const option = await store.dispatch("payOptions/determineOption", to.query.showoptions as string | undefined);
        await fetchInstallmentsData(deviceTag, option);
        // Backend login succeeded above -> safe to identify to Customer.io.
        identifyDevice(deviceTag);
        const country = authHelper.getSession()?.country;
        store.dispatch("payOptions/setCountry", country);
        initializeDatadogRum(country);
        if (country === Countries.PHILIPPINES) {
          next({ path: `/p/device/${deviceTag}`, query: to.query, replace: true });
          return;
        }
        next();
      } catch (error) {
        if (from.path === "/") {
          next();
          return;
        }
        console.error("Authentication or data fetching failed:", error);
        next(false);
      }
    },
  },
  {
    path: "/providers/",
    name: "ProvidersByDeviceTagAndCountry",
    component: PaymentProvidersView,
    props: true,
  },
  {
    path: "/views/:providerName",
    name: "DynamicProviderLoader",
    component: () => import("@/views/DynamicProviderLoaderView.vue"),
  },
  {
    path: "/other-amount",
    name: "OtherAmountView",
    component: OtherAmountEntryView,
  },
  {
    path: "/p/device/:deviceTag",
    name: "SuperPacmanInstallments",
    component: SuperPacmanInstallmentsView,
  },
  {
    path: "/payment-success",
    name: "PaymentSuccessView",
    component: PaymentSuccessView,
  },
  {
    path: "/payment-failed",
    name: "PaymentFailedView",
    component: PaymentFailedView,
  },
  {
    path: "/extensions/device/:deviceTag",
    name: "ExtensionTypeView",
    component: ExtensionTypeView,
    meta: { ownsHeader: true },
  },
  {
    path: "/extension-reason",
    name: "ExtensionReasonView",
    component: ExtensionReasonView,
    meta: { ownsHeader: true },
  },
  {
    path: "/v1/device/:deviceTag",
    name: "V1InstallmentsByDevice",
    component: V1InstallmentsView,
    props: true,
    beforeEnter: async (to) => await ensureV1DevAuth(to.params.deviceTag as string),
  },
  {
    path: "/v1/device/:deviceTag/providers",
    name: "V1ProvidersByDevice",
    component: V1PaymentProvidersView,
    props: true,
    beforeEnter: async (to) => await ensureV1DevAuth(to.params.deviceTag as string),
  },
  {
    path: "/v1/device/:deviceTag/providers/:groupId",
    name: "V1ProviderGroupByDevice",
    component: V1ProviderGroupView,
    props: true,
    beforeEnter: async (to) => await ensureV1DevAuth(to.params.deviceTag as string),
  },
  {
    path: "/v1/device/:deviceTag/other-amount",
    name: "V1OtherAmountByDevice",
    component: V1OtherAmountView,
    props: true,
    beforeEnter: async (to) => await ensureV1DevAuth(to.params.deviceTag as string),
  },
  {
    path: "/v1/device/:deviceTag/reference",
    name: "V1PaymentReferenceByDevice",
    component: V1PaymentReferenceView,
    props: true,
    beforeEnter: async (to) => await ensureV1DevAuth(to.params.deviceTag as string),
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

trackRouter(router);

export default router;
